<template>
  <div :class="['component-input-text', { focus }]">
    <div class="container-main">
      <img v-if="icon_" class="icon" :src="icon_" alt="" />

      <template v-if="type === 'datalist'">
        <datalist :id="datalistId">
          <option
            v-for="(item, index) in datalistOptions"
            :key="index"
            :value="item.value"
          >
            {{ item.label }}
          </option>
        </datalist>
      </template>

      <template v-if="type !== 'textarea'">
        <input
          v-if="mask_"
          ref="input"
          v-maska="mask_"
          v-model="localValue"
          :autocomplete="autocomplete"
          :class="{ 'validation-error': isInvalid }"
          :inputmode="inputmode_"
          :list="type === 'datalist' ? datalistId : false"
          :placeholder="placeholder"
          size="1"
          :type="type"
          @blur="inputBlur"
          @focus="inputFocus"
          @keyup.enter="keyupEnter"
        />
        <input
          v-else
          ref="input"
          :autocomplete="autocomplete"
          :class="{ 'validation-error': isInvalid }"
          :inputmode="inputmode_"
          :list="type === 'datalist' ? datalistId : false"
          :placeholder="placeholder"
          size="1"
          :type="type"
          :value="modelValue"
          @blur="inputBlur"
          @focus="inputFocus"
          @input="inputInput"
          @keyup.enter="keyupEnter"
        />
      </template>
      <template v-else>
        <textarea
          v-if="mask_"
          ref="input"
          v-maska="mask_"
          v-model="localValue"
          :class="{ 'validation-error': isInvalid }"
          cols="1"
          :placeholder="placeholder"
          rows="3"
          :type="type"
          @blur="inputBlur"
          @focus="inputFocus"
          @keyup.enter="keyupEnter"
        ></textarea>
        <textarea
          v-else
          ref="input"
          :class="{ 'validation-error': isInvalid }"
          cols="1"
          :placeholder="placeholder"
          rows="3"
          :type="type"
          :value="modelValue"
          @blur="inputBlur"
          @focus="inputFocus"
          @input="inputInput"
          @keyup.enter="keyupEnter"
        ></textarea>
      </template>

      <button
        v-if="showPasswordButton_"
        @click="buttonShowPasswordClick"
        aria-label="Mostrar/ocultar senha"
        type="button"
      >
        <img src="~@/assets/img/ico/ico_eye.svg" alt="" />
      </button>
    </div>
    <div v-if="modelValue && isInvalid" class="container-validation-message">
      Formato incorreto
    </div>
  </div>
</template>

<script>
import { vMaska } from "maska/vue";

export default {
  name: "ComponentInputText",
  directives: {
    maska: vMaska,
  },
  components: {},
  mixins: [],
  props: {
    datalistOptions: String,
    icon: String,
    maskPattern: [String, Array],
    // "off" by default: these fields hold other people's data (patients), not the user's own.
    autocomplete: { type: String, default: "off" },
    // Virtual keyboard; inferred from the type and the mask when not given.
    inputmode: String,
    placeholder: String,
    showPasswordButton: String,
    type: String,
    validation: String,
    modelValue: String, // Vue 3 v-model prop
  }, //components
  data() {
    return {
      focus: false,
      stInput: null,
      valid: null,
      touched: false,
      localValue: this.modelValue || '',
    };
  }, //data
  computed: {
    datalistId() {
      return `datalist_${this.$helpers.uuidv4()}`;
    },

    icon_() {
      let ret = false;

      if (this.icon) {
        ret = this.icon;
      }

      return ret;
    },

    // Phone and digit-only masks (CPF, date, time) open the numeric keypad on phones.
    inputmode_() {
      if (this.inputmode) return this.inputmode;
      if (this.type === "email") return "email";
      if (this.type === "tel") return "tel";
      if (this.type === "textarea" || this.type === "password") return undefined;
      const patterns = [].concat(this.maskPattern || []).map(String);
      if (!patterns.length) return undefined;
      if (patterns.every((p) => /^[#()\s.\-/:+]+$/.test(p))) {
        return patterns.some((p) => p.startsWith("(##)")) ? "tel" : "numeric";
      }
      return undefined;
    },

    mask_() {
      let ret = false;

      if (this.maskPattern) {
        // Maska v3 requires arrays to be wrapped in an object
        if (Array.isArray(this.maskPattern)) {
          ret = { mask: this.maskPattern };
        } else {
          ret = this.maskPattern;
        }
      }

      return ret;
    },

    // Only show the error after the user touched the field or tried to submit.
    isInvalid() {
      return this.touched && this.valid === false;
    },

    showPasswordButton_() {
      let ret = false;

      if (
        this.showPasswordButton === "true" ||
        this.showPasswordButton === true
      ) {
        ret = true;
      }

      return ret;
    },
  }, //methods
  watch: {
    modelValue(value, oldValue) {
      // Update localValue when parent changes modelValue (only for masked inputs)
      if (this.mask_ && value !== this.localValue) {
        this.localValue = value || '';
      }
      this.validate(value);
    },
    localValue(value, oldValue) {
      // Emit changes when localValue changes (for masked inputs)
      if (this.mask_ && value !== this.modelValue) {
        this.$emit("update:modelValue", value);
        this.validate(value);
      }
    },
  }, //watch
  created() {}, //created
  mounted() {
    this.validate(this.modelValue);
  }, //mounted
  updated() {}, //updated
  unmounted() {}, //computed
  methods: {
    validate(value) {
      const validation = this.validation;

      clearTimeout(this.stInput);

      this.stInput = setTimeout(() => {
        this.valid = this.$helpers.validateSingleField(value, validation);

        this.$emit("isValid", this.valid);
      }, 750);
    },

    inputBlur(e) {
      this.focus = false;
      this.touched = true;
    },

    inputFocus(e) {
      this.focus = true;
    },

    inputInput(e) {
      const value = e.currentTarget.value;

      this.$emit("update:modelValue", value); // Vue 3 v-model emit
      this.touched = true;

      // Validate right away: the debounced result below would leave a stale
      // "Formato incorreto" visible for 750 ms after the first keystroke.
      this.valid = this.$helpers.validateSingleField(value, this.validation);

      this.validate(value);
    },

    buttonShowPasswordClick(e) {
      if (this.$refs.input.type === "password") {
        this.$refs.input.type = "text";
      } else {
        this.$refs.input.type = "password";
      }
    },

    keyupEnter(e) {
      this.$emit("keyupEnter", e);
    },
  }, //unmounted
};
</script>

<style scoped>
.component-input-text {
  display: inline-block;
}

.component-input-text .container-main {
  display: inline-flex;
  align-items: center;

  background-color: #fff;
  border: 1px solid var(--border, #d0d0d0);
  border-radius: var(--radius-sm, 8px);
  box-sizing: border-box;
  min-height: 44px;
  padding: 0 0.85em;
  width: 100%;
}

.component-input-text .container-main input:not([type="checkbox"]):not([type="radio"]):not([type="range"]) {
  min-height: 0;
  height: 42px;
  padding: 0;
}

.component-input-text .container-main textarea {
  padding: 0.7em 0;
  line-height: 1.5;
  min-height: 0;
}

.component-input-text .container-main:has(.validation-error) {
  background-color: #fff5f5;
  border-color: #d00;
}

.component-input-text.focus .container-main {
  border-color: var(--brand, #29b1cc);
  box-shadow: 0 0 0 3px rgba(41, 177, 204, 0.15);
}

.component-input-text .container-main input:focus,
.component-input-text .container-main textarea:focus {
  box-shadow: none;
}

.component-input-text .container-validation-message {
  color: #d00;
  font-size: 0.7em;
  margin: 0;
  padding: 0.2em 0;
}

button {
  background-color: transparent;
  border: none;
  padding: 0;
}

input,
textarea {
  flex: 1;

  background-color: transparent;
  border: none;
}

input::-webkit-input-placeholder,
textarea::-webkit-input-placeholder {
  font-weight: 300;
  opacity: 0.5;
}

input::-moz-placeholder,
textarea::-moz-placeholder {
  font-weight: 300;
  opacity: 0.5;
}

input:-ms-input-placeholder,
textarea:-ms-input-placeholder {
  font-weight: 300;
  opacity: 0.5;
}

input::-ms-input-placeholder,
textarea::-ms-input-placeholder {
  font-weight: 300;
  opacity: 0.5;
}

input::placeholder,
textarea::placeholder {
  font-weight: 300;
  opacity: 0.5;
}

input.validation-error,
textarea.validation-error {
  color: #d00;
  background-color: transparent;
}

.icon {
  margin-right: 0.3em;
  max-height: 1em;
  width: 1.1em;
}

@media (max-width: 767px) {
}

@media (min-width: 768px) {
}

@media (min-width: 992px) {
}

@media (max-width: 1199px) {
}

@media (min-width: 1200px) {
  input,
  textarea {
    min-width: 6em;
  }
}
</style>
